import { Form, Formik } from 'formik'
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react'
import ReactTable from 'react-table'
import 'react-table/react-table.css'
import { toast } from 'react-toastify'
import Button from 'src/components/Button'
import FlexLayout from 'src/components/FlexLayout'
import FormField from 'src/components/Form/FormField'
import Loading from 'src/components/Loading'
import Text from 'src/components/Text'
import { TrashButton } from 'src/components/TrashButton'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import {
  getFractionableBatch,
  getFractionableBatches,
} from 'src/services/receivement/receivementService'
import '../../../App.css'
import FractionLot from '../components/FractionLot/FractionLot'
import useDeleteBatch from '../components/ModalDeleteBatch/useDeleteBatch'
import { Batch } from '../FractionTypes'
import * as S from './Fraction.styles'
import { formatBatchQuantities, orderBatchesFromOldestToNewest } from '../FractionUtils'
import { useDispatch } from 'react-redux'
import { actions } from '../FractionState'
import RaspberryIcon from '../../../assets/images/other-images/raspberry-pi.svg'
import { useSelector } from 'src/utils/store'
import PrinterInfo from '../components/PrinterInfo'
import { PrinterData } from 'src/features/settings/printers-management/PrintersManagementTypes'
import { PauseFractioning } from '../components/PauseFractioning'

const Fraction = () => {
  const [dataTable, setDataTables] = useState([])
  const [receivementId, setReceivementId] = useState<number | undefined>()
  const [modelId, setModelId] = useState<number | undefined>()
  const [load, setLoad] = useState(false)
  const [handleClickBatchFractionable, RemoveBatchFractionableModalComponent] = useDeleteBatch({ setState: setDataTables })
  const [lotFraction, setLotFraction] = useState<Batch>()
  const lotRef = useRef<HTMLInputElement>()
  const { fc, validateFc } = useFulfillmentCenter()
  const dispatch = useDispatch()
  const { isLocalRaspberryConnected, isRemotePrinterConnected, fractionTag } = useSelector((state) => state.fraction)

  useEffect(() => {
    validateFc()
  }, [validateFc])

  useEffect(() => {
    if (!lotFraction) {
      dispatch(actions.checkLocalRaspberryStatus())
    }
    const printerInfoData = localStorage.getItem('printerInfo')
    const printerInfo: PrinterData = printerInfoData ? JSON.parse(printerInfoData) : undefined
    if (!lotFraction && printerInfo) {
      dispatch(actions.checkRemotePrinterStatus())
    } else if (!printerInfo) {
      dispatch(actions.clearField('isRemotePrinterConnected'))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotFraction])

  useEffect(() => {
    if (isRemotePrinterConnected && fractionTag === undefined) {
      dispatch(actions.getActiveFractionTagData())
    } else if (!isRemotePrinterConnected) {
      dispatch(actions.clearField('fractionTag'))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRemotePrinterConnected])

  const updateBatchTable = useCallback(async () => {
    const result = await getFractionableBatches(fc.id)
    if (!result.isOk) {
      toast.error('Falha ao carregar os lotes')
      return
    }

    const orderedBatches = orderBatchesFromOldestToNewest(result.data)

    orderedBatches.forEach((batch) => {
      formatBatchQuantities(batch)
      batch.actions = (
        <TrashButton handleClick={() => handleClickBatchFractionable(batch)} />
      )
    })

    setDataTables(orderedBatches)
  }, [fc.id, handleClickBatchFractionable])

  const initializeBatchTable = useCallback(async () => {
    setLoad(true)

    await updateBatchTable()

    setLoad(false)

    lotRef.current?.focus()
  }, [updateBatchTable])

  useEffect(() => {
    initializeBatchTable()
  }, [initializeBatchTable])

  const handleFractionate = async (batchId) => {
    const result = await getFractionableBatch({
      batchId,
      fulfillmentCenterId: fc.id,
    })

    if (result.isOk) {
      const formattedBatch = formatBatchQuantities(result.data)
      setLotFraction(formattedBatch)
      setReceivementId(result.data.id_recebimento)
      setModelId(result.data.id_modelo)
    } else {
      toast.error(result.data.message)
      setLotFraction(undefined)
      setReceivementId(undefined)
    }
  }

  const onRowDoubleClick = (state, rowInfo) => ({
    onDoubleClick: () => {
      handleFractionate(rowInfo.row.id_lote)
    },
  })

  const handleFilter = async (values) => {
    await handleFractionate(values.lote)
  }

  const resetFraction = () => {
    setLotFraction(undefined)
  }

  return (
    <>
      <Loading loading={load} style={{ zIndex: '999' }} />
      <S.HeaderContainer>
        <S.SubHeaderContainer>
          <Text textType='header' text='fraction' />
          <S.HeaderPauseAndIconContainer>
            {lotFraction && (
              <PauseFractioning idBatch={lotFraction.id_lote} onPause={resetFraction} />
            )}
            {(lotFraction && (isLocalRaspberryConnected || isRemotePrinterConnected)) && (
              <S.HeaderIconContainer>
                <S.HeaderIcon src={RaspberryIcon} alt='header icon' data-testid='header-icon-test-id' />
              </S.HeaderIconContainer>
            )}
          </S.HeaderPauseAndIconContainer>
        </S.SubHeaderContainer>
      </S.HeaderContainer>
      {lotFraction ? (
        <FlexLayout>
          <FractionLot
            receivementId={receivementId}
            modelId={modelId}
            batch={lotFraction}
            resetFraction={resetFraction}
            updateTableLot={updateBatchTable}
            setLoad={setLoad}
          />
        </FlexLayout>
      ) : (
        <>
          <S.FormContainer>
            <Formik
              initialValues={{
                lote: '',
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validateOnMount
              onSubmit={handleFilter}
            >
              {() => (
                <Form style={{ width: '350px' }}>
                  <S.FormSubContainer>
                    <FormField
                      fieldRef={lotRef}
                      label='fraction.lot'
                      name='lote'
                    />
                    <Button
                      text='filter'
                      type='submit'
                      style={{ height: '40px', marginTop: '20px' }}
                    />
                  </S.FormSubContainer>
                </Form>
              )}
            </Formik>
            <PrinterInfo />
          </S.FormContainer>
          <FlexLayout
            style={{ margin: '20px 0 0 0', overflowY: 'auto', height: '550px' }}
          >
            <ReactTable
              style={{
                fontSize: '0.7em',
                maxWidth: '1250px',
                width: '100%',
                height: '100%',
                overflowX: 'scroll',
              }}
              data={dataTable}
              pageSizeOptions={[20, 30, 50, 100, 200, 500]}
              getTdProps={() => ({
                style: {
                  overflow: 'hidden',
                },
              })}
              columns={[
                {
                  Header: 'Id Lote',
                  id: 'id_lote',
                  accessor: 'id_lote',
                  width: 60,
                },
                {
                  Header: 'Fornecedor',
                  id: 'nome_fornecedor',
                  accessor: 'nome_fornecedor',
                  width: 200,
                },
                {
                  Header: 'Cód. Produto',
                  id: 'cod_produto',
                  accessor: 'cod_produto',
                  width: 100,
                },
                {
                  Header: 'Descrição',
                  id: 'descricao',
                  accessor: 'descricao',
                  width: 350,
                },
                {
                  Header: 'Total',
                  id: 'quantidade_total',
                  accessor: 'quantidade_total',
                  width: 60,
                  style: { textAlign: 'right' },
                },
                {
                  Header: 'Rejeitado',
                  id: 'quantidade_rejeitada',
                  accessor: 'quantidade_rejeitada',
                  width: 70,
                  style: { textAlign: 'right' },
                },
                {
                  Header: 'Util',
                  id: 'quantidade_util',
                  accessor: 'quantidade_util',
                  width: 60,
                  style: { textAlign: 'right' },
                },
                {
                  Header: 'Status',
                  id: 'status_fracionamento',
                  accessor: 'status_fracionamento',
                  width: 150,
                },
                {
                  Header: 'Dt Inclusão',
                  id: 'data_inclusao',
                  accessor: 'data_inclusao',
                  width: 100,
                  sortMethod: (firstDate, secondDate) => {
                    const firstDateTime = new Date(firstDate).getTime()
                    const secondDateTime = new Date(secondDate).getTime()
                    return secondDateTime > firstDateTime ? 1 : -1
                  },
                },
                {
                  Header: 'Ação',
                  id: 'actions',
                  accessor: 'actions',
                  width: 60,
                },
              ]}
              defaultPageSize={20}
              className='-striped -highlight'
              getTrProps={onRowDoubleClick}
              previousText='Anterior'
              nextText='Próxima'
              loadingText='Carregando...'
              noDataText='Sem registros'
              pageText='Página'
              ofText='de'
              rowsText='Registros'
            />
          </FlexLayout>
          {RemoveBatchFractionableModalComponent(lotFraction)}
        </>
      )}
    </>
  )
}

export default Fraction
